import { useEffect, useState } from "react";
import { Hash, Search, Save } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import TablePagination from "../../components/TablePagination";

const TOKENS = [
  { t: "{TAHUN}", d: "Tahun laporan (cth: 2026)" },
  { t: "{BULAN}", d: "Bulan laporan 2 digit (cth: 01)" },
  { t: "{NOPLAT}", d: "No. pendaftaran tanpa ruang (cth: WXY1234)" },
  { t: "{NO}", d: "Nombor turutan kenderaan (running number)" },
];

export default function SiriSettings() {
  const [data, setData] = useState(null);
  const [format, setFormat] = useState("");
  const [pad, setPad] = useState(3);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [edits, setEdits] = useState({});

  const load = () => api.get("/siri-settings").then((r) => {
    setData(r.data);
    setFormat(r.data.siri_format);
    setPad(r.data.siri_pad);
  });
  useEffect(() => { load(); }, []);
  useEffect(() => { setPage(1); }, [search]);

  const saveFormat = async () => {
    setSaving(true);
    try {
      await api.put("/settings", { siri_format: format, siri_pad: Number(pad) });
      toast.success("Format No. Siri disimpan");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const saveCounter = async (vehicleId) => {
    const val = Number(edits[vehicleId]);
    if (!val || val < 1) return toast.error("Nombor turutan mesti sekurang-kurangnya 1");
    try {
      await api.put(`/siri-settings/counters/${vehicleId}`, { next_no: val });
      toast.success("Nombor turutan dikemaskini");
      setEdits((e) => { const n = { ...e }; delete n[vehicleId]; return n; });
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const preview = format
    .replaceAll("{TAHUN}", String(new Date().getFullYear()))
    .replaceAll("{BULAN}", String(new Date().getMonth() + 1).padStart(2, "0"))
    .replaceAll("{NOPLAT}", "WXY1234")
    .replaceAll("{NO}", String(7).padStart(Number(pad) || 3, "0"));

  if (!data) return <p className="text-sm text-muted-foreground">Memuatkan...</p>;

  const filtered = data.vehicles.filter((v) => {
    const q = search.toLowerCase();
    return !q || `${v.registration_number} ${v.make} ${v.model}`.toLowerCase().includes(q);
  });
  const paged = filtered.slice((page - 1) * perPage, page * perPage);

  return (
    <div className="space-y-5" data-testid="siri-settings">
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading text-base flex items-center gap-2"><Hash className="h-4 w-4" /> Format No. Siri</CardTitle>
          <CardDescription>Digunakan pada laporan "Butir-Butir Penggunaan Kenderaan" (Jana Laporan) mengikut Pekeliling WP 4.1.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Templat Format</Label>
              <Input value={format} onChange={(e) => setFormat(e.target.value)} data-testid="siri-format-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Digit Nombor Turutan {"{NO}"}</Label>
              <Input type="number" min="1" max="6" value={pad} onChange={(e) => setPad(e.target.value)} data-testid="siri-pad-input" />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {TOKENS.map((tk) => (
              <button key={tk.t} type="button" title={tk.d} onClick={() => setFormat((f) => f + tk.t)}
                className="text-xs font-mono bg-slate-100 hover:bg-indigo-50 border border-slate-200 rounded-md px-2 py-1 transition-colors"
                data-testid={`siri-token-${tk.t.replace(/[{}]/g, "")}`}>
                {tk.t}
              </button>
            ))}
          </div>
          <div className="bg-indigo-50/60 border border-indigo-100 rounded-lg p-3 text-sm flex items-center gap-3">
            <span className="text-xs text-muted-foreground">Pratonton:</span>
            <Badge className="bg-[#10056E] hover:bg-[#10056E] font-mono" data-testid="siri-preview">{preview}</Badge>
          </div>
          <Button onClick={saveFormat} disabled={saving} className="bg-[#10056E] hover:bg-[#2a17a8]" data-testid="siri-save-format-btn">
            <Save className="h-4 w-4 mr-2" /> {saving ? "Menyimpan..." : "Simpan Format"}
          </Button>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading text-base">Nombor Turutan Setiap Kenderaan</CardTitle>
          <CardDescription>Nombor {"{NO}"} seterusnya yang akan diberikan apabila laporan dijana buat kali pertama bagi tahun berkenaan. No. Siri yang telah dijana kekal tidak berubah.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="px-4 py-3 border-b flex justify-end">
            <div className="relative w-full sm:w-64">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input className="pl-9 h-9" placeholder="Cari kenderaan..." value={search}
                onChange={(e) => setSearch(e.target.value)} data-testid="siri-search-input" />
            </div>
          </div>
          <TablePagination page={page} setPage={setPage} perPage={perPage} setPerPage={setPerPage} total={filtered.length} />
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/70">
                <TableHead>Kenderaan</TableHead>
                <TableHead>No. Siri Terkini Dijana</TableHead>
                <TableHead className="w-44">No. Turutan Seterusnya</TableHead>
                <TableHead className="w-24"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paged.map((v) => (
                <TableRow key={v.vehicle_id} data-testid={`siri-row-${v.vehicle_id}`}>
                  <TableCell>
                    <p className="text-sm font-semibold">{v.registration_number}</p>
                    <p className="text-xs text-muted-foreground">{v.make} {v.model} · {v.category}</p>
                  </TableCell>
                  <TableCell className="text-sm">
                    {v.last_siri ? <span className="font-mono text-xs">{v.last_siri} <span className="text-muted-foreground">({v.last_year})</span></span> : <span className="text-muted-foreground text-xs">Belum dijana</span>}
                  </TableCell>
                  <TableCell>
                    <Input type="number" min="1" className="h-8 w-28 font-mono"
                      value={edits[v.vehicle_id] ?? v.next_no}
                      onChange={(e) => setEdits({ ...edits, [v.vehicle_id]: e.target.value })}
                      data-testid={`siri-counter-input-${v.vehicle_id}`} />
                  </TableCell>
                  <TableCell>
                    {edits[v.vehicle_id] != null && Number(edits[v.vehicle_id]) !== v.next_no && (
                      <Button size="sm" className="h-8 bg-[#10056E] hover:bg-[#2a17a8]" onClick={() => saveCounter(v.vehicle_id)}
                        data-testid={`siri-counter-save-${v.vehicle_id}`}>Simpan</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
